<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Pathways"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
              <div class="col-lg-auto">
                <b-form-select v-model="category" :options="categoryOptions" class="w-auto" @change="fetchPathways()" />
              </div>

              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn
                  class="ml-2"
                  :to="{ name: 'sa-add-pathway' }"
                  exact
                  variant="primary"
                  v-text="'Add Pathway'"
                />
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="pathways"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
          >
            <template #cell(title)="data">
              <strong>{{ data.item.title }}</strong>
            </template>

            <template #cell(progression)="data">
              <strong>{{ get(data.item, 'from_program.title', '—') }}</strong>
              <md-icon class="mx-1" style="font-size: 14px;">arrow_forward</md-icon>
              <strong>{{ get(data.item, 'to_program.title', '—') }}</strong>
            </template>

            <template #cell(category)="data">
              {{ categoryLabel(data.item.category) }}
            </template>

            <template #cell(state)="data">
              {{ data.item.state || '—' }}
            </template>

            <template #cell(timeframe)="data">
              <span v-if="data.item.timeframe_value">
                {{ data.item.timeframe_value }} {{ data.item.timeframe_unit }}
              </span>
              <span v-else>—</span>
            </template>

            <template #cell(is_active)="data">
              <b-badge
                :class="`border border-${data.item.is_active ? 'success' : 'danger'} text-${
                  data.item.is_active ? 'success' : 'danger'
                }`"
                variant="none"
              >
                {{ data.item.is_active ? 'Active' : 'Inactive' }}
              </b-badge>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <router-link
                :to="{ name: 'sa-edit-pathway', params: { id: data.item.id } }"
                class="svg-icon mr-2"
                v-b-popover.hover.right
                :title="'Edit'"
              >
                <i class="material-icons">edit</i>
              </router-link>
              <a
                href="#"
                @click.prevent="toggleActive(data.index)"
                v-b-popover.hover.right
                :title="data.item.is_active ? 'Deactivate' : 'Activate'"
              >
                <md-icon :class="data.item.is_active ? 'text-danger' : 'text-success'">
                  {{ data.item.is_active ? 'block' : 'check_circle_outline' }}
                </md-icon>
              </a>
              <a href="#" @click.prevent="removePathway(data.item.id)" v-b-popover.hover.right :title="'Delete'">
                <i class="material-icons text-danger">delete</i>
              </a>
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalPathways"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="pathways-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

export default {
  components: { MdIcon, PageHeader, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Manage Pathways',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalPathways: 0,
      searchTerm: '',
      pathways: [],
      ordering: '',
      category: '',
      categoryOptions: [
        { value: '', text: 'All categories' },
        { value: 'nursing', text: 'Nursing' },
        { value: 'nursing_bridge', text: 'Nursing Bridge' },
        { value: 'allied_health', text: 'Allied Health' },
      ],
    };
  },

  computed: {
    tableColumns() {
      return [
        { key: 'title', label: 'Title' },
        { key: 'progression', label: 'From → To' },
        { key: 'category', label: 'Category' },
        { key: 'state', label: 'State' },
        { key: 'timeframe', label: 'Timeframe' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', tdClass: 'text-right', thStyle: { minWidth: '120px' } },
      ];
    },
  },

  methods: {
    get,
    ...mapActions('pathways', ['getAllPathways', 'updatePathway', 'deletePathway']),

    categoryLabel(c) {
      return (this.categoryOptions.find(o => o.value === c) || {}).text || '—';
    },

    async fetchPathways(pageNum = 1, params = {}) {
      this.isLoading = true;
      try {
        const response = await this.getAllPathways({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.category && { category: this.category }),
          ...params,
        });
        this.pathways = get(response, 'data.results', []);
        this.currentPage = pageNum;
        this.totalPathways = get(response, 'data.count', 0);
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchPathways(pageNum);
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchPathways();
    }, 500),

    async toggleActive(index) {
      const current = this.pathways[index];
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Mark pathway "${current.title}" as ${current.is_active ? 'Inactive' : 'Active'}?`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.updatePathway({ id: current.id, data: { is_active: !current.is_active } });
          this.fetchPathways(this.currentPage);
          this.makeToast({ variant: 'success', msg: 'Status updated.' });
        }
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async removePathway(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          'Please confirm that you want to delete this pathway.',
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deletePathway(id);
          this.fetchPathways(this.currentPage);
          this.makeToast({ variant: 'success', msg: 'Pathway deleted.' });
        }
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },

  async mounted() {
    this.fetchPathways();
  },
};
</script>
